/**
 * 🎯 PLAN NOTIFICATION COMPONENT
 * Displays plan change notifications in chat with action buttons
 * 
 * Handles:
 * - Plan creation "AHA!" moments
 * - Plan modification notifications
 * - Context saved confirmations
 * - Deep-linking to Move section
 * - Engagement tracking
 * 
 * Sprint 2 Implementation
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import './PlanNotificationComponent.css';

export interface PlanNotificationAction {
  type: string;
  text: string;
  url: string;
}

export interface PlanNotificationProps {
  message: string;
  notificationType: 'plan_created' | 'plan_modified' | 'plan_recalibrated' | 'context_saved' | 'goal_created_aha';
  planId?: string;
  actions?: PlanNotificationAction[];
  metadata?: {
    goal_title?: string;
    change_count?: number;
    change_types?: string[];
    aha_moment?: boolean;
    timestamp?: string;
  };
  onEngagement?: (actionType: string, planId?: string) => void;
}

const PlanNotificationComponent: React.FC<PlanNotificationProps> = ({
  message,
  notificationType,
  planId,
  actions = [],
  metadata = {},
  onEngagement
}) => {
  const navigate = useNavigate();

  const handleActionClick = (action: PlanNotificationAction) => {
    // Track engagement
    if (onEngagement) {
      onEngagement(action.type, planId);
    }

    // Handle navigation
    if (action.url.startsWith('/mypath')) {
      // MyPath navigation with deep-linking support
      const url = new URL(action.url, window.location.origin);
      
      // Add highlighting parameters for plan notifications
      if (planId) {
        url.searchParams.set('highlight', planId);
        url.searchParams.set('type', 'plan');
      }
      
      navigate(`${url.pathname}${url.search}`);
    } else if (action.url.startsWith('/')) {
      // Other internal navigation
      navigate(action.url);
    } else if (action.url.startsWith('#')) {
      // Anchor link (for highlighting specific items)
      const element = document.getElementById(action.url.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        element.classList.add('highlighted');
        setTimeout(() => element.classList.remove('highlighted'), 3000);
      }
    } else {
      // External URL (shouldn't happen but safe fallback)
      window.open(action.url, '_blank');
    }
  };

  const getNotificationIcon = () => {
    switch (notificationType) {
      case 'goal_created_aha':
        return '🎯';
      case 'plan_created':
        return '✨';
      case 'plan_modified':
        return '🔄';
      case 'plan_recalibrated':
        return '⚡';
      case 'context_saved':
        return '✅';
      default:
        return '📋';
    }
  };

  const getNotificationClass = () => {
    const baseClass = 'plan-notification';
    
    if (metadata.aha_moment) {
      return `${baseClass} ${baseClass}--aha-moment`;
    }
    
    return `${baseClass} ${baseClass}--${notificationType.replace('_', '-')}`;
  };

  return (
    <div className={getNotificationClass()}>
      <div className="plan-notification__header">
        <span className="plan-notification__icon">{getNotificationIcon()}</span>
        <span className="plan-notification__type">
          {metadata.aha_moment ? 'Goal Captured!' : 'Plan Update'}
        </span>
      </div>
      
      <div className="plan-notification__content">
        <p className="plan-notification__message">{message}</p>
        
        {metadata.goal_title && (
          <div className="plan-notification__goal">
            <strong>Goal:</strong> {metadata.goal_title}
          </div>
        )}
        
        {metadata.change_count && metadata.change_count > 0 && (
          <div className="plan-notification__changes">
            <strong>Changes:</strong> {metadata.change_count} modification{metadata.change_count > 1 ? 's' : ''}
            {metadata.change_types && metadata.change_types.length > 0 && (
              <span> ({metadata.change_types.join(', ')})</span>
            )}
          </div>
        )}
      </div>
      
      {actions.length > 0 && (
        <div className="plan-notification__actions">
          {actions.map((action, index) => (
            <button
              key={index}
              className={`plan-notification__action plan-notification__action--${action.type.replace('_', '-')}`}
              onClick={() => handleActionClick(action)}
            >
              {action.text}
            </button>
          ))}
        </div>
      )}
      
      {metadata.timestamp && (
        <div className="plan-notification__timestamp">
          {new Date(metadata.timestamp).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};

export default PlanNotificationComponent; 